import { useEffect, useState } from "react";
import axios from "axios";
import {
    FaUsers,
    FaCoins,
    FaChartBar,
    FaMoneyBill,
    FaChevronCircleLeft,
} from "react-icons/fa";
import { FaCircleChevronRight } from "react-icons/fa6";
import { IoSearch } from "react-icons/io5";
import { LuCoins } from "react-icons/lu";

import { useNavigate } from "react-router-dom";
import { FiUser } from "react-icons/fi";

const CoinEarnings = () => {
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [searchTimeout, setSearchTimeout] = useState(null);

    const [users, setUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);

    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);


    useEffect(() => {
        fetchData();
    }, [page]);

    const handleSearchChange = (value) => {
        setSearch(value);
        if (searchTimeout) clearTimeout(searchTimeout);

        const newTimeout = setTimeout(() => {
            setPage(1);
            fetchData(1, value);
        }, 800);
        setSearchTimeout(newTimeout);
    };

    const fetchData = async (pageNumber = page, searchQuery = search) => {
        try {
            setLoading(true);

            const overviewRes = await axios.get("/api/v1/app/coin-earnings/overview");
            if (overviewRes.data.status === "OK") {
                setOverview(overviewRes.data.data || null);
            }

            const userRes = await axios.get("/api/v1/user/getusersbycointransactions", {
                params: {
                    page: pageNumber,
                    limit,
                    search: searchQuery ? searchQuery : undefined,
                },
            });

            if (userRes.data.success) {
                setUsers(userRes.data.data || []);
                setTotalUsers(userRes.data.pagination.totalUsers);
                setTotalPages(userRes.data.pagination.totalPages);
            }
        } catch (err) {
            console.error("Error fetching:", err);
        } finally {
            setLoading(false);
        }
    };



    const formatNumber = (n) => (n ? n.toLocaleString("en-IN") : "N/A");

    if (loading) {
        return (
            <div className="p-10 flex justify-center items-center">
                <div className="w-10 h-10 border-4 border-gray-200 border-t-[var(--primary-solid)] rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 relative">
            <div className="bg-white border-2 border-gray-200 rounded-xl shadow-sm">

                {/* Header Section */}
                <div className="p-6">
                    <div className="flex justify-between items-center">
                        <h1 className="text-3xl md:text-4xl font-bold text-[var(--primary-solid)]">
                            Coin Earnings Management
                        </h1>
                    </div>
                    <p className="text-[var(--black-text)]/70 mt-2">
                        Manage user coin earnings, balances, and transactions
                    </p>
                </div>

                {/* Stats Section */}
                <div className="px-6 pb-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard
                    title="Total Coins"
                    value={`${formatNumber(overview?.overview?.totalCoinsEarned || 0)} coins`}
                    icon={<FaCoins />}
                    color="yellow"
                />
                <StatCard
                    title="Admin Credit"
                    value={formatNumber(overview?.earningsBySource?.ADMIN_CREDIT?.totalCoins || 0)}
                    icon={<FaUsers />}
                    color="green"
                />
                <StatCard
                    title="Purchase"
                    value={formatNumber(overview?.earningsBySource?.PURCHASE?.totalCoins || 0)}
                    icon={<FaChartBar />}
                    color="blue"
                />
                <StatCard
                    title="Available Balance"
                    value={`${formatNumber(overview?.overview?.totalAvailableCoins || 0)} coins`}
                    icon={<FaMoneyBill />}
                    color="purple"
                />
                    </div>
                </div>

                {/* Separator */}
                <div className="border-t border-gray-200"></div>

                {/* Table Section */}
                <div className="p-6">

                {/* TOP ROW */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">

                    <div>
                        <div className="flex items-center">
                            <h2 className="text-xl font-bold text-[var(--primary-solid)] flex items-center gap-2">
                                <LuCoins /> User Coin Earnings
                            </h2>
                        </div>
                        <p className="text-[var(--black-text)]/70">
                            Overview of user coin balances and earning activities
                        </p>
                    </div>


                    {/* SEARCH */}
                    <div className="relative w-full md:w-72">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--primary-solid)] text-xl">
                            <IoSearch />
                        </span>

                        <input
                            type="text"
                            placeholder="Search users ..."
                            value={search}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 rounded-xl border-2 border-gray-200 bg-white text-[var(--black-text)] 
                            placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)] 
                            focus:ring-2 focus:ring-[var(--yellow)]"
                        />
                    </div>
                </div>

                {/* USER LIST */}
                <div className="mt-4">
                    {users.length === 0 ? (
                        <p className="text-center text-gray-400 py-6">No users found</p>
                    ) : (
                        users.map((user) => (
                            <div
                                key={user._id}
                                onClick={() => navigate(`/coin-earnings/user/${user._id}`)}
                                className="border-2 border-gray-200 rounded-xl p-4 mb-4 shadow-sm bg-white
                                    hover:border-[var(--primary-solid)] hover:border-2 transition flex items-center gap-4"
                            >
                              
                                <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center">
    {user.profileImage && user.profileImage !== "default-profile.jpg" ? (
        <img
            src={user.profileImage}
            alt={user.fullName}
            className="w-full h-full object-cover"
            onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/default-avatar.png"; // optional fallback
            }}
        />
    ) : (
        <FiUser className="text-gray-500 text-xl" />
    )}
</div>

                                <div className="flex-1">
                                    <p className="font-bold text-[var(--black-text)] text-lg">{user.fullName || "N/A"}</p>
                                    <p className="text-[var(--black-text)]/70 text-sm">{user.phoneNumber}</p>
                                </div>

                                <button
                                    onClick={() => navigate(`/coin-earnings/user/${user._id}`)}
                                    className="text-[var(--primary-solid)] text-2xl hover:opacity-80"
                                >
                                    <FaCircleChevronRight />
                                </button>
                            </div>
                        ))
                    )}
                </div>

            </div>

            <div className="flex items-center justify-center mt-8">
                <div className="flex items-center gap-2 bg-white border-2 border-gray-200 px-4 py-3 rounded-xl shadow-sm">

                    <button
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                        className="px-3 py-2 rounded-lg text-sm font-semibold transition bg-[var(--yellow)] text-[var(--black)]
                        disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80"
                    >
                        <FaChevronCircleLeft />
                    </button>

                    <div className="flex items-center gap-2">
                        {(() => {
                            let pages = [];
                            const createPageBtn = (p) => (
                                <button
                                    key={p}
                                    onClick={() => setPage(p)}
                                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-semibold border transition 
                                        ${p === page
                                            ? "bg-gradient-to-r from-[var(--primary-solid)] to-[var(--primary-dark)] text-white border-gray-200"
                                            : "text-[var(--black-text)] border-2 border-gray-200 hover:bg-gradient-to-r hover:from-[var(--primary-solid)] hover:to-[var(--primary-dark)] hover:text-white"
                                        }`}
                                >
                                    {p}
                                </button>
                            );

                            const createDot = (key) => (
                                <span key={key} className="text-[var(--primary-solid)] font-bold px-2">…</span>
                            );

                            if (totalPages <= 7) {
                                for (let i = 1; i <= totalPages; i++) pages.push(createPageBtn(i));
                            } else {
                                pages.push(createPageBtn(1));
                                pages.push(createPageBtn(2));

                                if (page > 4) pages.push(createDot("start"));

                                let start = Math.max(3, page - 1);
                                let end = Math.min(totalPages - 2, page + 1);

                                for (let i = start; i <= end; i++) pages.push(createPageBtn(i));

                                if (page < totalPages - 3) pages.push(createDot("end"));

                                pages.push(createPageBtn(totalPages - 1));
                                pages.push(createPageBtn(totalPages));
                            }

                            return pages;
                        })()}
                    </div>

                    <button
                        disabled={page === totalPages}
                        onClick={() => setPage(page + 1)}
                        className="px-3 py-2 rounded-lg text-sm font-semibold transition bg-[var(--yellow)] text-[var(--black)]
                        disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80"
                    >
                        <FaCircleChevronRight />
                    </button>

                </div>
                </div>

            </div>
        </div>
    );
};

const StatCard = ({ title, value, icon, color = "yellow" }) => {
    const colorClasses = {
        yellow: {
            bg: "bg-gradient-to-br from-[var(--yellow)]/10 to-[var(--yellow)]/5 border-[var(--yellow)]/20",
            text: "text-[var(--yellow)]",
            icon: "text-[var(--yellow)]"
        },
        green: {
            bg: "bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20",
            text: "text-green-400",
            icon: "text-green-400"
        },
        blue: {
            bg: "bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20",
            text: "text-blue-400",
            icon: "text-blue-400"
        },
        purple: {
            bg: "bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20",
            text: "text-purple-400",
            icon: "text-purple-400"
        }
    };

    return (
        <div className={`${colorClasses[color].bg} rounded-xl p-4 shadow-lg border-2`}>
            <div className="flex items-center justify-between">
                <div>
                    <p className={`text-xs font-medium ${colorClasses[color].text} uppercase tracking-wider`}>
                        {title}
                    </p>
                    <p className={`text-2xl font-bold ${colorClasses[color].text}`}>
                        {value}
                    </p>
                </div>
                <div className={`text-3xl ${colorClasses[color].icon}`}>
                    {icon}
                </div>
            </div>
        </div>
    );
};

export default CoinEarnings;

